<template>
  <div class="gl-movements-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/stock">Stock</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Movement History</span>
          </div>
          <h1 class="gl-branch-title">Stock Movement History</h1>
        </div>
        <router-link to="/owner/stock" class="gl-back-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          <span class="gl-back-label">Back to Stock</span>
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Filter bar -->
      <div class="gl-filter-bar">
        <div class="gl-branch-select-wrap">
          <svg class="gl-branch-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <select class="gl-branch-select" v-model="branchId" @change="fetchMovements">
            <option value="">Select a branch…</option>
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
          </select>
          <svg class="gl-branch-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <select class="gl-filter-select" v-model="filters.type" @change="fetchMovements">
          <option value="">All Types</option>
          <option value="purchase">Purchase</option>
          <option value="sale">Sale</option>
          <option value="transfer_in">Transfer In</option>
          <option value="transfer_out">Transfer Out</option>
          <option value="waste">Waste</option>
          <option value="expired">Expired</option>
          <option value="adjustment">Adjustment</option>
          <option value="initial">Initial Stock</option>
        </select>

        <div class="gl-date-range">
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.start_date" @change="fetchMovements" placeholder="Start date">
          </div>
          <span class="gl-date-sep">—</span>
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.end_date" @change="fetchMovements" placeholder="End date">
          </div>
        </div>

        <button class="gl-filter-clear" @click="resetFilters" v-if="filters.type || filters.start_date || filters.end_date">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>

      <!-- No branch -->
      <div v-if="!branchId" class="gl-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <p>Select a branch above to view movement history</p>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="gl-loading">
        <div class="gl-spinner"></div>
        <span>Loading movements…</span>
      </div>

      <!-- Table -->
      <div v-else class="gl-table-card">

        <!-- Total count strip -->
        <div class="gl-count-strip" v-if="movements.length">
          <span class="gl-count-num">{{ pagination.total }}</span>
          <span class="gl-count-lbl">movement{{ pagination.total !== 1 ? 's' : '' }}</span>
          <span class="gl-count-filter" v-if="filters.type || filters.start_date || filters.end_date">— filtered</span>
        </div>

        <div v-if="!movements.length" class="gl-empty" style="padding: 60px 0;">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <p>No movement history found.</p>
        </div>

        <div v-else class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Product</th>
                <th>Type</th>
                <th class="th-right">Quantity</th>
                <th class="th-right">Balance After</th>
                <th>By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="movement in movements" :key="movement.id">
                <td class="td-datetime">
                  <div class="gl-mv-date">{{ formatDate(movement.created_at) }}</div>
                  <div class="gl-mv-time">{{ formatTime(movement.created_at) }}</div>
                </td>
                <td>
                  <div class="gl-mv-product">{{ movement.product?.name }}</div>
                  <div class="gl-mv-sku" v-if="movement.product?.sku">{{ movement.product.sku }}</div>
                </td>
                <td>
                  <span class="gl-type-badge" :class="`gl-type-${movement.type}`">
                    {{ formatType(movement.type) }}
                  </span>
                </td>
                <td class="td-right">
                  <span class="gl-qty" :class="movement.quantity > 0 ? 'gl-qty-pos' : 'gl-qty-neg'">
                    {{ movement.quantity > 0 ? '+' : '' }}{{ movement.quantity }}
                  </span>
                </td>
                <td class="td-right">
                  <span class="gl-balance">{{ movement.balance_after }}</span>
                </td>
                <td>
                  <span class="gl-mv-by">{{ movement.created_by?.name || 'System' }}</span>
                </td>
                <td>
                  <span class="gl-mv-notes" v-if="movement.notes">{{ movement.notes }}</span>
                  <span class="gl-mv-empty" v-else>—</span>
                  <div class="gl-mv-ref" v-if="movement.reference_number">Ref: {{ movement.reference_number }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="gl-pagination">
          <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchMovements(pagination.current_page - 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="gl-page-btn"
            :class="{ active: page === pagination.current_page }"
            @click="fetchMovements(page)"
          >{{ page }}</button>
          <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchMovements(pagination.current_page + 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ownerStockApi, ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'StockMovementHistory',

  data() {
    return {
      loading: false,
      branchId: '',
      branches: [],
      movements: [],
      filters: {
        type: '',
        start_date: '',
        end_date: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      }
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.pagination.last_page;
      const current = this.pagination.current_page;
      const delta = 2;
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  mounted() {
    this.fetchBranches();
    if (this.$route.params.branchId) {
      this.branchId = parseInt(this.$route.params.branchId);
      this.fetchMovements();
    }
  },

  methods: {
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatTime(dateStr) {
      return new Date(dateStr).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' });
    },

    formatType(type) {
      const types = {
        purchase: 'Purchase', sale: 'Sale',
        transfer_in: 'Transfer In', transfer_out: 'Transfer Out',
        waste: 'Waste', expired: 'Expired',
        adjustment: 'Adjustment', initial: 'Initial Stock'
      };
      return types[type] || type;
    },

    resetFilters() {
      this.filters = { type: '', start_date: '', end_date: '' };
      this.fetchMovements();
    },

    async fetchBranches() {
      try {
        const response = await ownerBranchApi.list({ per_page: 100 });
        this.branches = response.data.data;
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    },

    async fetchMovements(page = 1) {
      if (!this.branchId) return;
      try {
        this.loading = true;
        const response = await ownerStockApi.movements(this.branchId, { page, ...this.filters });
        this.movements = response.data.data;
        if (response.data.meta) {
          this.pagination = response.data.meta;
        }
      } catch (error) {
        console.error('Failed to fetch movements:', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page layout ── */
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

.gl-back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #374151; background: #fff;
  text-decoration: none; cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.1s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.gl-back-btn:hover { border-color: #d1d5db; background: #f9fafb; color: #111827; text-decoration: none; transform: translateX(-2px); }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none;
  padding: 0; margin-bottom: 16px; flex-wrap: wrap;
}

.gl-branch-select-wrap { position: relative; display: flex; align-items: center; flex-shrink: 0; }
.gl-branch-icon { position: absolute; left: 10px; color: #6b7280; pointer-events: none; z-index: 1; }
.gl-branch-chevron { position: absolute; right: 10px; color: #9ca3af; pointer-events: none; }
.gl-branch-select {
  appearance: none; -webkit-appearance: none;
  border: 1.5px solid #e8eaf0; border-radius: 9px;
  padding: 7px 32px 7px 30px; font-size: 13.5px; font-weight: 600;
  color: #111827; background: #f9fafb; cursor: pointer; outline: none;
  min-width: 180px; transition: border-color 0.15s;
}
.gl-branch-select:focus { border-color: #900C27; background: #fff; }

.gl-filter-select {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 12px;
  font-size: 13px; color: #374151; background: #fff; outline: none;
  cursor: pointer; transition: border-color 0.15s;
}
.gl-filter-select:focus { border-color: #900C27; }

/* Date range */
.gl-date-range { display: flex; align-items: center; gap: 8px; }
.gl-date-sep { color: #9ca3af; font-size: 13px; flex-shrink: 0; }
.gl-date-wrap { position: relative; display: flex; align-items: center; }
.gl-date-icon { position: absolute; left: 10px; color: #9ca3af; pointer-events: none; z-index: 1; }
.gl-date-input {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 10px 7px 28px;
  font-size: 13px; color: #374151; background: #fff; outline: none;
  cursor: pointer; transition: border-color 0.15s; min-width: 140px;
}
.gl-date-input:focus { border-color: #900C27; }

.gl-filter-clear {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px;
  background: #fff5f5; color: #dc2626; font-size: 12.5px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.gl-filter-clear:hover { background: #fee2e2; }

/* ── Empty / Loading ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 80px 0; color: #9ca3af; text-align: center; }
.gl-empty p { margin: 0; font-size: 14px; }
.gl-loading { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 60px 0; font-size: 14px; color: #9ca3af; }
.gl-spinner { width: 20px; height: 20px; border: 2px solid #f0f1f3; border-top-color: #900C27; border-radius: 50%; animation: gl-spin 0.7s linear infinite; flex-shrink: 0; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Table card ── */
.gl-table-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; margin-bottom: 24px; }

/* Count strip */
.gl-count-strip { display: flex; align-items: center; gap: 5px; padding: 11px 18px; border-bottom: 1px solid #f5f6f8; }
.gl-count-num { font-size: 13px; font-weight: 700; color: #111827; }
.gl-count-lbl { font-size: 13px; color: #6b7280; }
.gl-count-filter { font-size: 12px; color: #9ca3af; }

.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 11px 16px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-right { text-align: right; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 12px 16px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-right { text-align: right; }
.gl-table tbody td.td-datetime { white-space: nowrap; }

/* Date/time cell */
.gl-mv-date { font-size: 13px; font-weight: 500; color: #374151; }
.gl-mv-time { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }

/* Product cell */
.gl-mv-product { font-weight: 600; font-size: 13.5px; color: #111827; }
.gl-mv-sku { font-size: 11px; color: #9ca3af; margin-top: 1px; font-family: 'Courier New', monospace; }

/* Type badges */
.gl-type-badge { display: inline-block; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-type-purchase   { background: #dff0ec; color: #900C27; }
.gl-type-sale       { background: #dbeafe; color: #2563eb; }
.gl-type-transfer_in  { background: #e0f2fe; color: #0284c7; }
.gl-type-transfer_out { background: #fef3c7; color: #d97706; }
.gl-type-waste      { background: #fee2e2; color: #dc2626; }
.gl-type-expired    { background: #fee2e2; color: #dc2626; }
.gl-type-adjustment { background: #f3f4f6; color: #6b7280; }
.gl-type-initial    { background: #f1f5f9; color: #475569; }

/* Quantity */
.gl-qty { font-size: 13.5px; font-weight: 700; }
.gl-qty-pos { color: #900C27; }
.gl-qty-neg { color: #dc2626; }

/* Balance */
.gl-balance { font-size: 13.5px; font-weight: 600; color: #111827; }

/* By */
.gl-mv-by { font-size: 13px; color: #6b7280; }

/* Notes */
.gl-mv-notes { font-size: 12.5px; color: #6b7280; }
.gl-mv-empty { font-size: 13px; color: #d1d5db; }
.gl-mv-ref { font-size: 11.5px; color: #900C27; margin-top: 2px; font-family: 'Courier New', monospace; }

/* Pagination */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 14px; border-top: 1px solid #f0f1f3; }
.gl-page-btn { min-width: 32px; height: 32px; padding: 0 8px; border-radius: 7px; border: 1.5px solid #e8eaf0; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.12s; }
.gl-page-btn:hover:not(:disabled) { border-color: #a7c4be; background: #f0f5f4; }
.gl-page-btn.active { background: #900C27; border-color: #900C27; color: #fff; font-weight: 700; }
.gl-page-btn:disabled { opacity: 0.38; cursor: not-allowed; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  /* Header */
  .gl-branch-title { font-size: 18px; }
  .gl-back-label { display: none; }
  .gl-back-btn { padding: 9px 12px; }

  /* Filter bar */
  .gl-filter-bar { flex-direction: column; align-items: stretch; gap: 8px; padding: 0; }
  .gl-branch-select-wrap { width: 100%; }
  .gl-branch-select { width: 100%; min-width: unset; }
  .gl-filter-select { width: 100%; }
  .gl-date-range { flex-direction: column; align-items: stretch; gap: 8px; width: 100%; }
  .gl-date-sep { display: none; }
  .gl-date-wrap { width: 100%; }
  .gl-date-input { width: 100%; min-width: unset; box-sizing: border-box; }

  /* Table — hide Balance After (col 5), By (col 6), Notes (col 7) */
  .gl-table thead th:nth-child(5),
  .gl-table tbody td:nth-child(5),
  .gl-table thead th:nth-child(6),
  .gl-table tbody td:nth-child(6),
  .gl-table thead th:nth-child(7),
  .gl-table tbody td:nth-child(7) { display: none; }

  /* Tighten remaining cells */
  .gl-table thead th,
  .gl-table tbody td { padding: 10px 10px; }
  .gl-mv-product { font-size: 12.5px; }
  .gl-type-badge { font-size: 11px; padding: 2px 7px; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #d1d5db; }
body.dark-only .gl-back-btn { background: #1e2130; border-color: #2d3249; color: #d1d5db; box-shadow: none; }
body.dark-only .gl-back-btn:hover { background: #262932; color: #f3f4f6; }

body.dark-only .gl-branch-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-filter-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-date-input { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-date-icon { color: #6b7280; }

body.dark-only .gl-table-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-count-strip { border-color: #2d3249; }
body.dark-only .gl-count-num { color: #f3f4f6; }
body.dark-only .gl-count-lbl { color: #9ca3af; }
body.dark-only .gl-table thead tr { background: #252837; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-color: #2a2f46; }
body.dark-only .gl-table tbody tr:hover { background: #252837; }
body.dark-only .gl-table tbody td { color: #e5e7eb; }
body.dark-only .gl-mv-date { color: #d1d5db; }
body.dark-only .gl-mv-product { color: #f3f4f6; }
body.dark-only .gl-balance { color: #f3f4f6; }
body.dark-only .gl-type-adjustment { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-type-initial { background: #252837; color: #94a3b8; }

body.dark-only .gl-pagination { border-color: #2d3249; }
body.dark-only .gl-page-btn { border-color: #2d3249; background: #1e2130; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { background: #1a2e1e; border-color: #6f0a1e; }
</style>
